// src/components/km/ResourceDettaglio.jsx
//
// FE-01 / SIT-022 (UC-CD-01) — 15/10: contenuto UNICO del dettaglio risorsa,
// montato sia dal pannello laterale di MultiFitting sia dalla finestra al
// centro della Console HR (CandidatoDettaglioModal). Prima i due ingressi
// mostravano blocchi diversi in ordine diverso; ora cambia solo il
// contenitore (pannello / finestra) e il blocco di contesto in testa:
//   - da un annuncio: punteggio, ragionamento, requisiti, fitting sugli altri
//     annunci del run (costruito da MultiFitting, che ha già la riga in
//     memoria);
//   - dalla Console: il selettore di annuncio (ResourceFittingPanel), lasciato
//     com'era per decisione del 15/10, in fondo come prima (`bloccoInFondo`):
//     la parte fissa resta identica e il fitting non ne spezza l'ordine.
// Parte fissa, identica e nello stesso ordine:
//   Profilo → Self-Assessment → Esperienze → Stato della risorsa → Anagrafica.
//
// Profilo: dati di GET /resources/{id}. Precedenza, campo per campo:
//   1. anagrafica (`anagrafica`): valore confermato da HR (UC-CD-02);
//   2. riga MultiFitting (`riga`), quando si entra da un annuncio;
//   3. CV (`da_cv`): valore proposto dall'ATS, non ancora confermato.
// Competenza principale/secondaria e voto CV: dalla riga MultiFitting o dal
// blocco `da_cv_info` di /resources/{id} (sola lettura, fuori dal
// precompilato dell'anagrafica; Armando, 15/10). Campi null se mancano,
// blocco null se la risorsa non ha un CV.
//
// Esperienze: elenco da GET /esperienze?resource_id=<id KM o cvid> (Armando,
// 15/10): il raccordo risorsa → consulente lo fa il server, lista vuota se la
// risorsa non ha un consulente. Se la chiamata fallisce (es. 404 per una
// risorsa sconosciuta) si ripiega sui conteggi di `ciclo_vita`.
//
// Comportamenti invariati: invio/sollecito SA, transizioni di stato e modifica
// anagrafica restano nei rispettivi componenti, con gli stessi id e callback.
// Novità: sola lettura per Inbound e Da cancellare anche dalla Console
// (prima solo in MultiFitting, SIT-023).

import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useRealKmClient } from '../../lib/useRealKmClient.js';
import ResourceSelfAssessmentPanel from './ResourceSelfAssessmentPanel.jsx';
import ResourceStateManager from './ResourceStateManager.jsx';
import ResourceAnagraficaEditor from './ResourceAnagraficaEditor.jsx';
import { statoRisorsaLabel } from './kmLabels.js';

const T = {
  border: 'rgba(0,0,0,0.10)',
  surface2: '#F1F0ED',
  txt: '#1A1916',
  txt2: '#6B6A65',
  txt3: '#A3A19C',
  accent: '#185FA5',
  accentBg: '#E6F1FB',
  accentTxt: '#0C447C',
  greenBg: '#EAF3DE',
  greenTxt: '#27500A',
  redBg: '#FCEBEB',
  redTxt: '#791F1F',
};

const STATI_SOLA_LETTURA = ['R1', 'R4'];

const stileSezione = { padding: '12px 18px', borderBottom: `0.5px solid ${T.border}` };
const stileEtichetta = { fontSize: 11, fontWeight: 600, color: T.txt3, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 };

function Sezione({ titolo, children, ultima }) {
  return (
    <div style={ultima ? { ...stileSezione, borderBottom: 'none' } : stileSezione}>
      <div style={stileEtichetta}>{titolo}</div>
      {children}
    </div>
  );
}

function primo(...valori) {
  return valori.find((v) => v !== undefined && v !== null && v !== '' && !(Array.isArray(v) && v.length === 0));
}

function comeLista(v) {
  if (!v) return [];
  if (Array.isArray(v)) return v.map((x) => String(x).trim()).filter(Boolean);
  return String(v).split(',').map((x) => x.trim()).filter(Boolean);
}

function formatCosto(numero, testo) {
  if (numero !== undefined && numero !== null && numero !== '') return String(numero);
  return testo || null;
}

function fmtData(ts) {
  if (!ts) return null;
  const d = new Date(ts);
  return Number.isNaN(d.getTime()) ? String(ts) : d.toLocaleDateString('it-IT');
}

export default function ResourceDettaglio({
  resourceId,          // id con cui il chiamante conosce la risorsa (cvid da MultiFitting, candidato_id dalla Console)
  jdId,                // annuncio di contesto, solo da MultiFitting
  riga,                // riga MultiFitting (facoltativa): competenze, costi, voto CV già in memoria
  statoCicloVita,      // stato già noto al chiamante, per non attendere la fetch
  bloccoContesto,      // nodo React in testa (fitting sull'annuncio, da MultiFitting)
  bloccoInFondo,       // nodo React in fondo (selettore annuncio, dalla Console)
  onResourceUpdated,
}) {
  const kmClient = useRealKmClient();
  const [risorsa, setRisorsa] = useState(null);
  const [statoFetch, setStatoFetch] = useState('loading'); // loading | ready | error
  const [esperienze, setEsperienze] = useState(null);      // null = elenco non disponibile

  useEffect(() => {
    if (!resourceId) return undefined;
    let annullato = false;
    setStatoFetch('loading');
    kmClient.get(`/resources/${encodeURIComponent(resourceId)}`)
      .then((d) => { if (!annullato) { setRisorsa(d || null); setStatoFetch('ready'); } })
      .catch(() => { if (!annullato) setStatoFetch('error'); });
    return () => { annullato = true; };
  }, [kmClient, resourceId]);

  useEffect(() => {
    if (!resourceId) { setEsperienze(null); return undefined; }
    let annullato = false;
    kmClient.get('/esperienze', { query: { resource_id: resourceId, ordina: 'data_inizio', direzione: 'desc', limite: 20 } })
      .then((d) => { if (!annullato) setEsperienze(Array.isArray(d?.esperienze) ? d.esperienze : []); })
      .catch(() => { if (!annullato) setEsperienze(null); });
    return () => { annullato = true; };
  }, [kmClient, resourceId]);

  const anag = risorsa?.anagrafica || {};
  const cv = risorsa?.da_cv || {};
  const cvInfo = risorsa?.da_cv_info || {};
  const r = riga || {};

  const email = primo(anag.email, r.candidate_email, cv.email);
  const telefono = primo(anag.wa_phone, cv.wa_phone);
  const competenzaPrincipale = primo(r.competenza_principale, cvInfo.competenza_principale);
  const competenzaSecondaria = primo(r.competenza_secondaria, cvInfo.competenza_secondaria);
  const certificazioni = comeLista(primo(anag.certificazioni, r.certificazioni, cv.certificazioni));
  const localita = primo(anag.regione, r.localita, cv.regione);
  const costoOnsite = primo(
    formatCosto(anag.tariffa_listino_onsite), r.costo_onsite,
    formatCosto(cv.tariffa_listino_onsite, cv.tariffa_listino_onsite_testo),
  );
  const costoRemoto = primo(
    formatCosto(anag.tariffa_listino_remoto), r.costo_remoto,
    formatCosto(cv.tariffa_listino_remoto, cv.tariffa_listino_remoto_testo),
  );
  const disponibilita = primo(anag.disponibilita_contrattuale, r.disponibilita, cv.disponibilita_contrattuale);
  // 23/10 — SAL 16/10, azione 24: «Voto HR» (campo Voto CV di Altamira).
  const votoHr = primo(r.voto_hr, r.voto_cv, cvInfo.voto_cv);
  // 16/10: seniority del profilo (modificabile da HR nell'anagrafica).
  const seniority = risorsa?.profilo?.seniority || null;

  const stato = primo(statoCicloVita, risorsa?.stato_ciclo_vita);
  const solaLettura = STATI_SOLA_LETTURA.includes(stato);
  const ciclo = risorsa?.ciclo_vita || null;

  const caricamento = statoFetch === 'loading' && !riga;
  const nessunaCompetenza = !competenzaPrincipale && !competenzaSecondaria && certificazioni.length === 0 && !seniority;

  return (
    <div>
      {bloccoContesto}

      {/* ── Profilo ── */}
      <Sezione titolo="Contatti">
        {caricamento ? <div style={{ fontSize: 12.5, color: T.txt3 }}>Caricamento…</div> : (
          <>
            <div style={{ fontSize: 13, color: T.txt2 }}>✉ {email || '—'}</div>
            {telefono && <div style={{ fontSize: 13, color: T.txt2, marginTop: 2 }}>☎ {telefono}</div>}
          </>
        )}
      </Sezione>

      <Sezione titolo="Competenze">
        {nessunaCompetenza ? (
          <div style={{ fontSize: 12.5, color: T.txt3 }}>{caricamento ? 'Caricamento…' : 'Non indicate nel CV.'}</div>
        ) : (
          <>
            <div style={{ fontSize: 13 }}>Principale: <strong>{competenzaPrincipale || '—'}</strong></div>
            <div style={{ fontSize: 13, color: T.txt2, marginTop: 2 }}>Secondaria: {competenzaSecondaria || '—'}</div>
            {seniority && (
              <div style={{ fontSize: 13, color: T.txt2, marginTop: 2 }}>Seniority: {seniority.charAt(0).toUpperCase() + seniority.slice(1)}</div>
            )}
            {certificazioni.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 8 }}>
                {certificazioni.map((c) => (
                  <span key={c} style={{ fontSize: 11, padding: '3px 8px', borderRadius: 12, background: T.surface2, border: `0.5px solid ${T.border}`, color: T.txt2 }}>{c}</span>
                ))}
              </div>
            )}
          </>
        )}
      </Sezione>

      <Sezione titolo="Logistica e costi">
        <div style={{ fontSize: 13, marginBottom: 4 }}>📍 {localita || '—'}</div>
        {(costoOnsite || costoRemoto) && (
          <div style={{ fontSize: 13, marginBottom: 4 }}>💶 On-site: <strong>{costoOnsite || '—'}</strong> · Remote: {costoRemoto || '—'}</div>
        )}
        {disponibilita && <div style={{ fontSize: 12, color: T.txt2 }}>🕐 {disponibilita}</div>}
        {votoHr && (
          <div style={{ marginTop: 6, fontSize: 12, color: T.txt2 }}>
            Voto HR{' '}
            <span title="Giudizio complessivo sulla persona, indipendente dal fitting"
              style={{ padding: '2px 9px', borderRadius: 6, fontSize: 12, border: `0.5px solid ${T.border}`, background: '#FFFFFF', color: T.txt, fontWeight: 500 }}>{votoHr}</span>
          </div>
        )}
      </Sezione>

      {/* ── Self-Assessment ── */}
      <div style={{ padding: '12px 18px 0', borderBottom: `0.5px solid ${T.border}` }}>
        <ResourceSelfAssessmentPanel resourceId={resourceId} jdId={jdId} showApriEsperienza onInvioInviato={onResourceUpdated} mostraConteggioEsperienze={false} />
      </div>

      {/* ── Esperienze ── */}
      <Sezione titolo="Esperienze">
        {Array.isArray(esperienze) ? (
          esperienze.length === 0 ? (
            <div style={{ fontSize: 12.5, color: T.txt3 }}>Nessuna esperienza registrata.</div>
          ) : esperienze.map((e) => {
            const inCorso = e.stato === 'aperta';
            const fine = fmtData(inCorso ? e.data_chiusura_prevista : (e.data_chiusura || e.data_chiusura_prevista));
            return (
              <div key={e.esperienza_id} style={{ border: `0.5px solid ${T.border}`, borderRadius: 8, padding: '8px 10px', marginBottom: 6, fontSize: 12.5 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                  <strong>{e.progetto || e.cliente || 'Esperienza'}</strong>
                  <span style={{ padding: '2px 8px', borderRadius: 20, fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap', ...(inCorso ? { background: T.greenBg, color: T.greenTxt } : { background: T.surface2, color: T.txt2 }) }}>
                    {inCorso ? 'In corso' : 'Chiusa'}
                  </span>
                </div>
                <div style={{ color: T.txt2 }}>
                  {[e.cliente, fmtData(e.data_inizio) && `dal ${fmtData(e.data_inizio)}`, fine && (inCorso ? `fine prevista ${fine}` : `al ${fine}`)].filter(Boolean).join(' · ')}
                </div>
                <Link to={`/knowledge-management/esperienze/${e.esperienza_id}`} style={{ color: T.accent, fontSize: 12 }}>Esperienza →</Link>
              </div>
            );
          })
        ) : ciclo ? (
          <div style={{ fontSize: 13, color: T.txt2 }}>
            {ciclo.esperienze_totali > 0
              ? `${ciclo.esperienze_totali} ${ciclo.esperienze_totali === 1 ? 'esperienza' : 'esperienze'}, di cui ${ciclo.esperienze_attive || 0} in corso`
              : 'Nessuna esperienza registrata.'}
          </div>
        ) : (
          <div style={{ fontSize: 12.5, color: T.txt3 }}>{statoFetch === 'loading' ? 'Caricamento…' : 'Dato non disponibile.'}</div>
        )}
      </Sezione>

      {/* ── Stato della risorsa ── */}
      <div style={{ padding: '12px 18px 0', borderBottom: `0.5px solid ${T.border}` }}>
        {solaLettura ? (
          <div style={{ padding: '0 0 12px', fontSize: 12, color: T.txt2, fontStyle: 'italic' }}>
            Sola lettura — risorsa in stato {statoRisorsaLabel(stato, risorsa?.sottostato)}
          </div>
        ) : (
          <ResourceStateManager resourceId={resourceId} onStateChanged={onResourceUpdated} />
        )}
      </div>

      {/* ── Anagrafica e dati commerciali ── */}
      <div style={bloccoInFondo ? { padding: '12px 18px 18px', borderBottom: `0.5px solid ${T.border}` } : { padding: '12px 18px 18px' }}>
        <ResourceAnagraficaEditor resourceId={resourceId} />
      </div>

      {bloccoInFondo}
    </div>
  );
}
